import { Link } from "react-router-dom";
import { ArrowLeft, Heart, Award, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const teamMembers = [
  {
    id: "1",
    name: "Paulette McGraw",
    role: "Owner & Operator",
    bio: "Paulette founded River City Cleaners with a passion for helping families and businesses maintain clean, healthy spaces. With years of experience in the cleaning industry and a commitment to excellence, she leads our team with dedication and a personal touch that sets us apart.",
    image: "https://customer-assets.emergentagent.com/job_fresh-sweep-7/artifacts/vbgrrx29_20250910_085504%280%29.jpg"
  },
  {
    id: "2",
    name: "Shukurat Jimoh",
    role: "Lead Cleaner",
    bio: "Shukurat brings exceptional attention to detail and a warm, positive energy to every job. As our Lead Cleaner, she ensures every space meets our high standards and mentors our team to deliver consistently outstanding results.",
    image: "https://customer-assets.emergentagent.com/job_fresh-sweep-7/artifacts/lu9w0zi8_1000025375.jpg"
  },
  {
    id: "3",
    name: "Owolabi Jimoh",
    role: "Accounting & Scheduling",
    bio: "Owolabi keeps our operations running smoothly behind the scenes. From managing schedules to handling accounts, his organizational skills and attention to detail ensure our clients receive timely, reliable service every time.",
    image: "https://customer-assets.emergentagent.com/job_fresh-sweep-7/artifacts/9eabb6lk_1000025376.jpg"
  },
  {
    id: "4",
    name: "Linda Hughes",
    role: "Manager of Morale & Team Building",
    bio: "Linda is the heart of our team culture. With her infectious positivity and dedication to team wellness, she ensures our crew stays motivated, connected, and proud of the work they do. A happy team means happy clients!",
    image: "https://customer-assets.emergentagent.com/job_fresh-sweep-7/artifacts/w2pis4lk_1000025377.jpg"
  }
];

const OurTeam = () => {
  // Filter to only show team members with images
  const displayedMembers = teamMembers.filter(member => member.image);

  return (
    <div className="min-h-screen bg-white" data-testid="team-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#2563EB] hover:text-[#1D4ED8] mb-8 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block bg-[#2563EB]/10 text-[#2563EB] px-4 py-2 rounded-full text-sm font-medium mb-4">
              Our Team
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Meet the People Behind
              <span className="gradient-text block">The Sparkle</span>
            </h1>
            <p className="text-lg text-slate-600">
              Our dedicated team brings passion, professionalism, and attention to detail 
              to every home and business we serve. Get to know the people who make 
              River City Cleaners special.
            </p>
          </div>
        </div>
      </section>

      {/* Team Members Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Owner/Operator Feature */}
          <div className="mb-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <img
                  src={teamMembers[0].image}
                  alt={teamMembers[0].name}
                  className="w-full h-[500px] object-cover rounded-3xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#2563EB] rounded-xl flex items-center justify-center">
                      <Heart className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Founder</p>
                      <p className="text-sm text-slate-500">Since Day One</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <span className="text-[#2563EB] font-medium text-sm uppercase tracking-wider">
                  Leadership
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-2">
                  {teamMembers[0].name}
                </h2>
                <p className="text-[#2563EB] font-semibold text-lg mb-6">
                  {teamMembers[0].role}
                </p>
                <p className="text-slate-600 text-lg leading-relaxed mb-8">
                  {teamMembers[0].bio}
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                    <Award className="w-6 h-6 text-[#2563EB]" />
                    <div>
                      <p className="font-semibold text-slate-900">Committed to Excellence</p>
                      <p className="text-sm text-slate-500">Quality in every detail</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                    <Users className="w-6 h-6 text-[#2563EB]" />
                    <div>
                      <p className="font-semibold text-slate-900">Community Focused</p>
                      <p className="text-sm text-slate-500">Serving Northern NB</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Other Team Members */}
          {displayedMembers.length > 1 && (
            <>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-slate-900">
                  Our Cleaning Specialists
                </h2>
                <p className="text-slate-600 mt-2">
                  The talented individuals who bring the sparkle to your space
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {displayedMembers.slice(1).map((member) => (
                  <div
                    key={member.id}
                    className="group bg-white rounded-3xl overflow-hidden shadow-lg border border-slate-100 hover:shadow-xl transition-shadow"
                    data-testid={`team-member-${member.id}`}
                  >
                    <div className="aspect-[4/5] overflow-hidden">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-slate-900">{member.name}</h3>
                      <p className="text-[#2563EB] font-medium mb-3">{member.role}</p>
                      <p className="text-slate-600 text-sm">{member.bio}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Join Our Team CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Want to Join Our Team?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            We're always looking for dedicated individuals who share our passion for 
            creating clean, healthy spaces. If you take pride in your work and love 
            helping others, we'd love to hear from you.
          </p>
          <Button
            onClick={() => window.location.href = "/#contact"}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 py-6 text-lg font-semibold"
            data-testid="join-team-btn"
          >
            Contact Us About Opportunities
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OurTeam;
